import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Card, message, Typography, Tabs, Modal, Divider } from 'antd';
import { UserOutlined, LockOutlined, MobileOutlined, WechatOutlined, QrcodeOutlined, SafetyOutlined } from '@ant-design/icons';
import client from '../api/client';
import useAuthStore from '../stores/authStore';
import { useBrandStore } from '../stores/brandStore';
import { useOemStore } from '../stores/oemStore';
import SmsCodeInput from '../components/SmsCodeInput';
import { fetchWechatAuthUrl } from '../api/wechat';
import type { User } from '@dtwin/shared';

function useLoginTarget() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const redirect = searchParams.get('redirect');

  const onAuthed = (user: User, token: string) => {
    login(user, token);
    let target = '/';
    if (redirect) {
      try {
        const u = new URL(redirect, window.location.origin);
        target = u.pathname + u.search;
      } catch {
        target = redirect.startsWith('/') ? redirect : '/';
      }
      // 安全白名单：仅允许业务列表页；拒绝编辑器/预览/发布路径，防止串号进入他人项目
      const isForbiddenPath = /^\/(editor|design|preview|publish)\//.test(target);
      if (isForbiddenPath) target = '/';
    }
    navigate(target, { replace: true });
  };
  return { onAuthed };
}

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const brand = useBrandStore((s) => s.config);
  const [wechatLoading, setWechatLoading] = useState(false);
  const [smsForm] = Form.useForm();
  const [pwdForm] = Form.useForm();
  const { onAuthed } = useLoginTarget();
  const smsPhone = Form.useWatch('phoneNumber', smsForm) || '';

  /** F-07：跳转微信开放平台扫码授权（snsapi_login） */
  const handleWechatLogin = async () => {
    setWechatLoading(true);
    try {
      const res = await fetchWechatAuthUrl();
      if (res.enabled && res.url) {
        window.location.href = res.url;
      } else {
        message.warning('微信登录未启用（请联系管理员配置）');
      }
    } catch {
      message.error('微信登录暂不可用，请稍后重试');
    } finally {
      setWechatLoading(false);
    }
  };

  const errMsg = (err: unknown) =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    || (err as Error)?.message
    || String(err);

  // 通道1：手机号+短信验证码（默认）
  const onSmsFinish = async (values: { phoneNumber: string; smsCode: string }) => {
    setLoading(true);
    try {
      const res = await client.post('/auth/sms/login', values);
      const { user, token } = res.data as { user: User; token: string };
      if (!token) return message.error('登录失败：服务器未返回令牌');
      onAuthed(user, token);
    } catch (err) {
      message.error(`登录失败：${errMsg(err)}`);
    } finally {
      setLoading(false);
    }
  };

  // 通道2：用户名/手机号+密码（备选，兼容老用户）｜§2.7 防爆破：needSms 梯度验证码 / locked 短信解锁
  const [needSms, setNeedSms] = useState(false);
  const [locked, setLocked] = useState<{ lockRemainingSeconds: number } | null>(null);
  const [unlockVisible, setUnlockVisible] = useState(false);
  const [unlockForm] = Form.useForm();
  const unlockPhone = Form.useWatch('phoneNumber', unlockForm) || '';
  // needSms 场景：密码登录无手机号，需用户填写绑定手机号才能获取验证码（后端按绑定号校验）
  const needSmsPhone = Form.useWatch('smsPhone', pwdForm) || '';

  // 图形验证码（防机器人）：服务端启用时返回题目；未启用/请求失败则不显示
  const [captcha, setCaptcha] = useState<{ captchaId: string; question: string } | null>(null);
  const refreshCaptcha = async () => {
    try {
      const r = await client.get('/auth/captcha');
      setCaptcha(r.data);
    } catch {
      setCaptcha(null);
    }
  };
  useEffect(() => {
    void refreshCaptcha();
  }, []);

  const onPwdFinish = async (values: { username: string; password: string; smsCode?: string; smsPhone?: string; captchaAnswer?: string }) => {
    setLoading(true);
    try {
      const res = await client.post('/auth/login', {
        username: values.username,
        password: values.password,
        smsCode: values.smsCode || undefined,
        smsPhone: values.smsPhone || undefined,
        captchaId: captcha?.captchaId,
        captchaAnswer: values.captchaAnswer,
      });
      const { user, token } = res.data as { user: User; token: string };
      if (!token) return message.error('登录失败：服务器未返回令牌');
      onAuthed(user, token);
    } catch (err) {
      void refreshCaptcha(); // 验证码一次性：失败后自动换题
      const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      if (data?.needSms && !data?.locked) {
        setNeedSms(true);
        message.warning('登录失败次数过多，请输入短信验证码后重试');
      } else if (data?.locked) {
        setLocked({ lockRemainingSeconds: Number(data.lockRemainingSeconds) || 0 });
        message.error(String(data.message || '账号已锁定'));
      } else {
        const msg =
          (data as { message?: string })?.message
          || (err as Error)?.message
          || '登录失败';
        message.error(`登录失败：${msg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const onUnlockFinish = async (values: { phoneNumber: string; smsCode: string }) => {
    setLoading(true);
    try {
      const username = pwdForm.getFieldValue('username');
      await client.post('/auth/unlock', {
        username,
        phoneNumber: values.phoneNumber,
        smsCode: values.smsCode,
      });
      message.success('解锁成功，请重新登录');
      setLocked(null);
      setNeedSms(false);
      setUnlockVisible(false);
      unlockForm.resetFields();
    } catch (err) {
      message.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const oem = useOemStore((s) => s.oem);
  const oemBgStyle: React.CSSProperties =
    oem?.enabled !== false && oem?.loginBg
      ? { background: `url(${oem.loginBg}) center/cover no-repeat, #0f0f1a` }
      : { background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)' };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        ...oemBgStyle,
        position: 'relative',
      }}
      data-testid="login-page"
    >
      {/* 备案信息（按域名区分）：www.dtwince.com→桂ICP备19007458号-3；www.dtwince.cloud→桂ICP备19007458号-4 */}
      <div style={{ position: 'absolute', bottom: 14, width: '100%', textAlign: 'center', fontSize: 12, color: '#6a7a90' }}>
        <span>{brand?.loginFooter ?? '© 2026 DTwince'}</span>
        <span style={{ margin: '0 8px', color: '#444' }}>|</span>
        <a
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noreferrer"
          style={{ color: '#7a8aa0', textDecoration: 'none' }}
        >
          {window.location.hostname.endsWith('dtwince.com')
            ? '桂ICP备19007458号-3'
            : '桂ICP备19007458号-4'}
        </a>
      </div>
      <Card
        title={
          <Typography.Title level={4} style={{ margin: 0, textAlign: 'center' }}>
            {brand?.name ?? 'DTwince 云边智孪'}
          </Typography.Title>
        }
        style={{ width: 400, background: '#1a1a2e', borderColor: '#333' }}
      >
        <Tabs
          defaultActiveKey="sms"
          items={[
            {
              key: 'sms',
              label: '手机号登录',
              children: (
                <Form form={smsForm} onFinish={onSmsFinish} size="large">
                  <Form.Item
                    name="phoneNumber"
                    rules={[
                      { required: true, message: '请输入手机号' },
                      { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' },
                    ]}
                  >
                    <Input prefix={<MobileOutlined />} placeholder="手机号" maxLength={11} />
                  </Form.Item>
                  <Form.Item name="smsCode" rules={[{ required: true, message: '请输入验证码' }]}>
                    <SmsCodeInput scene="login" phoneNumber={smsPhone} />
                  </Form.Item>
                  <Form.Item style={{ marginBottom: 8 }}>
                    <Button type="primary" htmlType="submit" loading={loading} block>
                      登录
                    </Button>
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: 'pwd',
              label: '密码登录',
              children: (
                <Form form={pwdForm} onFinish={onPwdFinish} size="large">
                  <Form.Item name="username" rules={[{ required: true, message: '请输入用户名或手机号' }]}>
                    <Input prefix={<UserOutlined />} placeholder="用户名或手机号" />
                  </Form.Item>
                  <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
                    <Input.Password prefix={<LockOutlined />} placeholder="密码" />
                  </Form.Item>
                  {captcha && (
                    <Form.Item name="captchaAnswer" rules={[{ required: true, message: '请输入验证码' }]}>
                      <Input
                        prefix={<SafetyOutlined />}
                        placeholder={`验证码：${captcha.question}`}
                        autoComplete="off"
                        suffix={
                          <a
                            onClick={(e) => { e.preventDefault(); void refreshCaptcha(); }}
                            style={{ fontSize: 12 }}
                            data-testid="captcha-refresh"
                          >
                            换一题
                          </a>
                        }
                      />
                    </Form.Item>
                  )}
                  {needSms && !locked && (
                    <>
                      <Form.Item
                        name="smsPhone"
                        rules={[
                          { required: true, message: '请输入绑定手机号' },
                          { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' },
                        ]}
                      >
                        <Input prefix={<MobileOutlined />} placeholder="绑定手机号（接收验证码）" maxLength={11} />
                      </Form.Item>
                      <Form.Item name="smsCode" rules={[{ required: true, message: '请输入短信验证码' }]}>
                        <SmsCodeInput scene="login" phoneNumber={needSmsPhone} />
                      </Form.Item>
                    </>
                  )}
                  {locked && (
                    <div style={{ marginBottom: 12, color: '#ff4d4f', fontSize: 13 }}>
                      账号已锁定，请{' '}
                      <a onClick={() => setUnlockVisible(true)}>短信解锁</a>
                      {' '}或等待 {Math.ceil(locked.lockRemainingSeconds / 60)} 分钟后重试
                    </div>
                  )}
                  <Form.Item style={{ marginBottom: 8 }}>
                    <Button type="primary" htmlType="submit" loading={loading} block>
                      登录
                    </Button>
                  </Form.Item>
                </Form>
              ),
            },
          ]}
        />
        <div style={{ textAlign: 'center', marginTop: 4 }}>
          没有账号？<a href="/register">立即注册</a>
          <span style={{ margin: '0 8px', color: '#555' }}>|</span>
          <a href="/forgot-password">忘记密码</a>
        </div>
        {/* F-07：微信扫码登录（开放平台网站应用） */}
        <Divider plain style={{ borderColor: '#333', color: '#666', fontSize: 12, margin: '16px 0 12px' }}>其他登录方式</Divider>
        <Button
          block
          size="large"
          loading={wechatLoading}
          onClick={handleWechatLogin}
          icon={<WechatOutlined style={{ color: '#07c160' }} />}
          data-testid="wechat-login-btn"
        >
          微信扫码登录
        </Button>
        {/* F-06：设备互扫登录（已登录设备确认，新设备登录） */}
        <Button
          block
          size="large"
          style={{ marginTop: 10 }}
          onClick={() => navigate('/qr-login')}
          icon={<QrcodeOutlined style={{ color: '#4e9eff' }} />}
          data-testid="qr-login-entry"
        >
          设备扫码登录
        </Button>
      </Card>
      <Modal
        title="短信解锁"
        open={unlockVisible}
        onCancel={() => setUnlockVisible(false)}
        footer={null}
      >
        <Form form={unlockForm} onFinish={onUnlockFinish} size="large">
          <Form.Item
            name="phoneNumber"
            rules={[
              { required: true, message: '请输入绑定手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' },
            ]}
          >
            <Input prefix={<MobileOutlined />} placeholder="绑定手机号" maxLength={11} />
          </Form.Item>
          <Form.Item name="smsCode" rules={[{ required: true, message: '请输入验证码' }]}>
            <SmsCodeInput scene="login" phoneNumber={unlockPhone} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block>
            解锁
          </Button>
        </Form>
      </Modal>
    </div>
  );
}